/**
 * W5 — offers "Switch vehicles" after the server refused a Start/Resume with ALREADY_HAS_ACTIVE_TIMER.
 *
 * The refusal itself is unchanged (the existing toast still says a timer is running). This host only adds the
 * next step the founder asked for: one hands-on timer, switchable. It listens for OFFER_WORK_SWITCH_EVENT,
 * asks the server what is running (my_hands_on_now — never a client guess) and, on the person's confirmation,
 * calls switch_my_work: A is paused (SWITCHED, measured time kept), optionally left on a machine (unattended,
 * no labor), and B starts or resumes — all in one transaction, or nothing changes.
 *
 * Mounted once on the Dashboard. Where it is not mounted nothing happens beyond the existing toast.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  OFFER_WORK_SWITCH_EVENT, UNATTENDED_KINDS, newRequestId, readMyHandsOnNow, switchMyWork, unattendedKindLabel,
  workSwitchOutcomeCopy, type HandsOnNow, type OfferWorkSwitchDetail, type UnattendedKind,
} from '@/lib/timer/workSwitch';
import { requestServerFinish } from '@/utils/serverFinishIntent';

function ticketLabel(n: number | null | undefined, id?: string): string {
  return n != null ? `TKT-${String(n).padStart(4, '0')}` : id ? `ticket ${id.slice(0, 8)}` : 'that ticket';
}

export function WorkSwitchHost({ shopId }: { shopId: string | null | undefined }) {
  const [offer, setOffer] = useState<OfferWorkSwitchDetail | null>(null);
  const [current, setCurrent] = useState<HandsOnNow | null>(null);
  const [leaveMachine, setLeaveMachine] = useState(false);
  const [kind, setKind] = useState<UnattendedKind>('AC_CYCLE');
  const [minutes, setMinutes] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const requestId = useRef<string | null>(null);

  useEffect(() => {
    const onOffer = (e: Event) => {
      const detail = (e as CustomEvent<OfferWorkSwitchDetail>).detail;
      const sid = detail?.shopId ?? shopId;
      if (!detail?.toTicketId || !sid) return;
      void readMyHandsOnNow(sid).then((h) => {
        // Only a running TICKET timer can be switched; an inspection timer is paused from the inspection.
        if (!h.active || h.source !== 'ticket_timer' || h.ticketId === detail.toTicketId) return;
        requestId.current = newRequestId();
        setLeaveMachine(false); setKind('AC_CYCLE'); setMinutes(''); setNote('');
        setCurrent(h);
        setOffer({ ...detail, shopId: sid });
      });
    };
    window.addEventListener(OFFER_WORK_SWITCH_EVENT, onOffer);
    return () => window.removeEventListener(OFFER_WORK_SWITCH_EVENT, onOffer);
  }, [shopId]);

  const close = () => { setOffer(null); setCurrent(null); };
  const m = minutes.trim() === '' ? null : Number(minutes);
  const invalid = leaveMachine && ((m != null && (!Number.isInteger(m) || m < 1 || m > 1440)) || (kind === 'OTHER' && note.trim() === ''));

  const submit = async () => {
    if (!offer || !current?.ticketId) return;
    setBusy(true);
    try {
      const r = await switchMyWork({
        requestId: requestId.current ?? (requestId.current = newRequestId()),
        fromTicketId: current.ticketId,
        toTicketId: offer.toTicketId,
        expectedFromSessionId: current.sessionId ?? null,
        toJobIds: offer.toJobIds,
        unattended: leaveMachine ? { kind, expectedMinutes: m, kindNote: note.trim() || null } : null,
      });
      const c = workSwitchOutcomeCopy(r.outcome);
      (c.tone === 'success' ? toast.success : c.tone === 'warning' ? toast.warning : toast.error)(c.title, { description: c.description });
      if (r.outcome === 'SWITCHED') {
        if ('to' in r && r.to.outcome === 'STARTED' && r.to.ticketBecameRunning) {
          void requestServerFinish(r.to.ticketId, 'timer_started', { reason: 'w5 switch_my_work started the target' });
        }
        window.dispatchEvent(new Event('ticket-updated'));
        close();
      } else if (r.outcome !== 'FAILED') {
        // A definitive refusal: the request id is spent; a new act gets a new id.
        requestId.current = newRequestId();
        window.dispatchEvent(new Event('ticket-updated'));
        if (r.outcome === 'STATE_CHANGED' || r.outcome === 'NO_ACTIVE_TIMER') close();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!offer} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="work-switch-dialog">
        <DialogHeader>
          <DialogTitle>Switch vehicles?</DialogTitle>
          <DialogDescription>
            You are working hands-on on {ticketLabel(current?.ticketNumber, current?.ticketId)}. One hands-on timer runs at a time:
            switching pauses it (its measured time is kept) and starts {offer ? 'work on this vehicle' : ''}.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={leaveMachine} onChange={(e) => setLeaveMachine(e.target.checked)} />
          Leave {ticketLabel(current?.ticketNumber, current?.ticketId)} running on a machine (unattended — no labor recorded)
        </label>
        {leaveMachine && (
          <div className="grid gap-2">
            <div className="grid gap-1">
              <Label htmlFor="w5-switch-kind">Machine process</Label>
              <select id="w5-switch-kind" className="h-9 rounded-md border bg-background px-2 text-sm" value={kind}
                onChange={(e) => setKind(e.target.value as UnattendedKind)}>
                {UNATTENDED_KINDS.map((k) => <option key={k} value={k}>{unattendedKindLabel(k)}</option>)}
              </select>
            </div>
            <div className="grid gap-1">
              <Label htmlFor="w5-switch-min">Expected minutes (optional)</Label>
              <Input id="w5-switch-min" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
            </div>
            {kind === 'OTHER' && (
              <div className="grid gap-1">
                <Label htmlFor="w5-switch-note">What is the machine doing?</Label>
                <Input id="w5-switch-note" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={close}>Keep working on {ticketLabel(current?.ticketNumber, current?.ticketId)}</Button>
          <Button disabled={busy || invalid} onClick={() => void submit()} data-testid="work-switch-confirm">Switch</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WorkSwitchHost;
